import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import { XButton } from '../../components/XButton';
import XDateField from '../../components/XDateField';
import {
  batchCreateDomainConfigs,
  domainConfigsDataProvider,
  importDomainConfigsCsv,
} from './dataProvider';
import { healthCheckProxyDataProvider } from './health-check-proxy/dataProvider';
import { HealthCheckProxy } from './health-check-proxy/types';
import { DomainConfig } from './types';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import ErrorPage403 from 'src/pages/error/403';
import { Resources } from '../resource';
import AuditLogDialog from 'src/pages/admin/components/AuditLog/AuditLogDialog';

const T = 'module.domainConfigs';
const TP = 'module.healthCheckProxy';

const PROTOCOL_OPTIONS = ['http', 'https', 'socks4', 'socks5'] as const;
const PROXY_HOST_REGEX =
  /^(localhost|(([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,})|(\d{1,3}\.){3}\d{1,3})$/;

// ---------------------------------------------------------------------------
// Health Check Proxy – Add / Edit dialog
// ---------------------------------------------------------------------------

type ProxyFormData = {
  protocol: string;
  host: string;
  port: string;
  username: string;
  password: string;
  enabled: boolean;
};

type ProxyDialogProps = {
  open: boolean;
  initial?: HealthCheckProxy | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: (data: {
    protocol: string;
    host: string;
    port: number;
    username: string;
    password: string;
    enabled: boolean;
  }) => void;
};

function ProxyDialog({ open, initial, loading, onClose, onConfirm }: ProxyDialogProps) {
  const translate = useTranslate();
  const [form, setForm] = useState<ProxyFormData>({
    protocol: '',
    host: '',
    port: '',
    username: '',
    password: '',
    enabled: true,
  });
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        protocol: initial?.protocol ?? '',
        host: initial?.host ?? '',
        port: initial?.port != null ? String(initial.port) : '',
        username: initial?.username ?? '',
        password: initial?.password ?? '',
        enabled: initial?.enabled ?? true,
      });
      setTouched(false);
    }
  }, [open, initial]);

  const protocolError = touched && !form.protocol;
  const hostError = touched && !PROXY_HOST_REGEX.test(form.host.trim());
  const portError =
    touched &&
    (!form.port || isNaN(Number(form.port)) || Number(form.port) <= 0 || Number(form.port) > 65535);
  const usernameError = touched && !form.username.trim();
  const passwordError = touched && !form.password;

  const handleConfirm = () => {
    setTouched(true);
    if (
      !form.protocol ||
      !PROXY_HOST_REGEX.test(form.host.trim()) ||
      !form.port ||
      isNaN(Number(form.port)) ||
      Number(form.port) <= 0 ||
      Number(form.port) > 65535 ||
      !form.username.trim() ||
      !form.password
    )
      return;
    onConfirm({
      protocol: form.protocol.trim(),
      host: form.host.trim(),
      port: Number(form.port),
      username: form.username.trim(),
      password: form.password,
      enabled: form.enabled,
    });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{translate(initial ? `${TP}.editProxy` : `${TP}.addProxy`)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            fullWidth
            size="small"
            label={translate(`${TP}.protocol`)}
            value={form.protocol}
            onChange={(e) => {
              setForm((f) => ({ ...f, protocol: e.target.value }));
              setTouched(true);
            }}
            error={protocolError}
            helperText={protocolError ? translate(`${TP}.invalidProtocol`) : undefined}
          >
            {PROTOCOL_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={translate(`${TP}.host`)}
            value={form.host}
            onChange={(e) => {
              setForm((f) => ({ ...f, host: e.target.value }));
              setTouched(true);
            }}
            error={hostError}
            helperText={hostError ? translate(`${TP}.invalidHost`) : undefined}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label={translate(`${TP}.port`)}
            value={form.port}
            onChange={(e) => {
              setForm((f) => ({ ...f, port: e.target.value }));
              setTouched(true);
            }}
            error={portError}
            helperText={portError ? translate(`${TP}.invalidPort`) : undefined}
          />
          <TextField
            fullWidth
            size="small"
            label={translate(`${TP}.username`)}
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            error={usernameError}
            helperText={usernameError ? translate(`${TP}.requiredUsername`) : undefined}
          />
          <TextField
            fullWidth
            size="small"
            type="password"
            label={translate(`${TP}.password`)}
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            error={passwordError}
            helperText={passwordError ? translate(`${TP}.requiredPassword`) : undefined}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            }
            label={translate(`${TP}.enabled`)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {translate('ra.action.cancel')}
        </Button>
        <LoadingButton variant="contained" loading={loading} onClick={handleConfirm}>
          {translate('ra.action.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Add / Edit dialog
// ---------------------------------------------------------------------------

type DomainDialogProps = {
  open: boolean;
  initial?: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (domain: string) => void;
};

const DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

function isValidDomain(v: string) {
  return DOMAIN_REGEX.test(v.trim());
}

function DomainDialog({ open, initial = '', loading, onClose, onConfirm }: DomainDialogProps) {
  const translate = useTranslate();
  const [value, setValue] = useState(initial);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (open) {
      setValue(initial);
      setTouched(false);
    }
  }, [open, initial]);

  const error = touched && !isValidDomain(value);

  const handleConfirm = () => {
    setTouched(true);
    if (isValidDomain(value)) onConfirm(value.trim());
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{translate(initial ? `${T}.editDomain` : `${T}.addDomain`)}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={translate(`${T}.domain`)}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setTouched(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleConfirm();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              onClose();
            }
          }}
          error={error}
          helperText={error ? translate(`${T}.invalidDomain`) : undefined}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {translate('ra.action.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={handleConfirm}
          disabled={!value.trim()}
        >
          {translate('ra.action.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Batch create dialog
// ---------------------------------------------------------------------------

function BatchDialog({
  open,
  loading,
  onClose,
  onConfirm,
}: {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: (domains: string[]) => void;
}) {
  const translate = useTranslate();
  const [text, setText] = useState('');
  useEffect(() => {
    if (open) setText('');
  }, [open]);

  const domains = text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{translate(`${T}.batchDialog.title`)}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {translate(`${T}.batchDialog.hint`)}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={6}
          size="small"
          label={translate(`${T}.batchDialog.label`)}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={'example.com\nfoo.io\nbar.net'}
        />
        {domains.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {translate(`${T}.batchDialog.count`, { count: domains.length })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {translate('ra.action.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={() => onConfirm(domains)}
          disabled={domains.length === 0}
        >
          {translate(`${T}.batchDialog.addAll`)}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main List
// ---------------------------------------------------------------------------

export default function DomainConfigList() {
  const notify = useNotify();
  const translate = useTranslate();
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  // ---- Health Check Proxy state ----
  const [proxyRecords, setProxyRecords] = useState<HealthCheckProxy[]>([]);
  const [loadingProxyList, setLoadingProxyList] = useState(false);
  const [proxyDialogOpen, setProxyDialogOpen] = useState(false);
  const [proxyDialogLoading, setProxyDialogLoading] = useState(false);
  const [editingProxy, setEditingProxy] = useState<HealthCheckProxy | null>(null);
  const [deleteProxyLoading, setDeleteProxyLoading] = useState<string | null>(null);

  const fetchProxyList = useCallback(async () => {
    setLoadingProxyList(true);
    try {
      const result = await healthCheckProxyDataProvider.getList('', {
        pagination: { page: 1, perPage: 200 },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: {},
      });
      setProxyRecords(result.data);
    } catch (e: any) {
      notify(e?.message || translate(`${TP}.notify.loadError`), { type: 'error' });
    } finally {
      setLoadingProxyList(false);
    }
  }, [notify, translate]);

  useEffect(() => {
    fetchProxyList();
  }, [fetchProxyList]);

  const handleProxyDialogConfirm = async (data: {
    protocol: string;
    host: string;
    port: number;
    username: string;
    password: string;
    enabled: boolean;
  }) => {
    setProxyDialogLoading(true);
    try {
      if (editingProxy) {
        await healthCheckProxyDataProvider.update('', { id: editingProxy.id, data });
        notify(translate(`${TP}.notify.updated`), { type: 'success' });
      } else {
        await healthCheckProxyDataProvider.create('', { data });
        notify(translate(`${TP}.notify.created`), { type: 'success' });
      }
      setProxyDialogOpen(false);
      fetchProxyList();
    } catch (e: any) {
      notify(e?.message || translate(`${TP}.notify.operationFailed`), { type: 'error' });
    } finally {
      setProxyDialogLoading(false);
    }
  };

  const handleDeleteProxy = async (id: string) => {
    if (!window.confirm(translate(`${TP}.confirm.delete`))) return;
    setDeleteProxyLoading(id);
    try {
      await healthCheckProxyDataProvider.delete('', { id });
      notify(translate(`${TP}.notify.deleted`), { type: 'success' });
      fetchProxyList();
    } catch (e: any) {
      notify(e?.message || translate(`${TP}.notify.deleteFailed`), { type: 'error' });
    } finally {
      setDeleteProxyLoading(null);
    }
  };

  // ---- Domain Configs state ----
  const [records, setRecords] = useState<DomainConfig[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  // add/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [editing, setEditing] = useState<DomainConfig | null>(null);

  // batch dialog
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);

  // CSV import
  const [csvLoading, setCsvLoading] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // delete
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const result = await domainConfigsDataProvider.getList('', {
        pagination: { page: 1, perPage: 200 },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: {},
      });
      setRecords(result.data);
    } catch (e: any) {
      notify(e?.message || translate(`${T}.notify.loadError`), { type: 'error' });
    } finally {
      setLoadingList(false);
    }
  }, [notify, translate]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // ---- CRUD handlers ----

  const handleOpenAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const handleOpenEdit = (row: DomainConfig) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const handleDialogConfirm = async (domain: string) => {
    if (!domain.trim()) return;
    setDialogLoading(true);
    try {
      if (editing) {
        await domainConfigsDataProvider.update('', { id: editing.id, data: { domain } });
        notify(translate(`${T}.notify.updated`), { type: 'success' });
      } else {
        await domainConfigsDataProvider.create('', { data: { domain } });
        notify(translate(`${T}.notify.created`), { type: 'success' });
      }
      setDialogOpen(false);
      fetchList();
    } catch (e: any) {
      notify(e?.message || translate(`${T}.notify.operationFailed`), { type: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(translate(`${T}.confirm.delete`))) return;
    setDeleteLoading(id);
    try {
      await domainConfigsDataProvider.delete('', { id });
      notify(translate(`${T}.notify.deleted`), { type: 'success' });
      fetchList();
    } catch (e: any) {
      notify(e?.message || translate(`${T}.notify.deleteFailed`), { type: 'error' });
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleBatchConfirm = async (domains: string[]) => {
    setBatchLoading(true);
    try {
      await batchCreateDomainConfigs(domains);
      notify(translate(`${T}.notify.batchSuccess`, { count: domains.length }), { type: 'success' });
      setBatchOpen(false);
      fetchList();
    } catch (e: any) {
      notify(e?.message || translate(`${T}.notify.batchFailed`), { type: 'error' });
    } finally {
      setBatchLoading(false);
    }
  };

  const handleCsvChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setCsvLoading(true);
    try {
      await importDomainConfigsCsv(file);
      notify(translate(`${T}.notify.csvSuccess`), { type: 'success' });
      fetchList();
    } catch (err: any) {
      notify(err?.message || translate(`${T}.notify.csvFailed`), { type: 'error' });
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <PermissionGuard resource={Resources.domainConfigs} action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate(`${T}.title`)}
          action={
            <PermissionGuard resource="audit-logs" action="read" fallback={<></>} loadingFallback={<></>}>
              <Button
                variant="contained"
                size="medium"
                color="secondary"
                startIcon={<Iconify icon="ph:note" />}
                onClick={() => setAuditLogOpen(true)}
              >
                {translate('resource.auditLogs')}
              </Button>
            </PermissionGuard>
          }
        />

        {/* ---- Health Check Proxy section ---- */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 3, mb: 1 }}
        >
          <Typography variant="h6">{translate(`${TP}.title`)}</Typography>
          <PermissionButton resource={Resources.domainConfigs} action="create">
            <XButton
              variant="contained"
              label={translate(`${TP}.addProxy`)}
              onClick={() => {
                setEditingProxy(null);
                setProxyDialogOpen(true);
              }}
              sx={{ minWidth: 120 }}
            />
          </PermissionButton>
        </Stack>
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate(`${TP}.protocol`)}</TableCell>
                  <TableCell>{translate(`${TP}.host`)}</TableCell>
                  <TableCell>{translate(`${TP}.port`)}</TableCell>
                  <TableCell>{translate(`${TP}.username`)}</TableCell>
                  <TableCell>{translate(`${TP}.enabled`)}</TableCell>
                  <TableCell>{translate(`${TP}.createdAt`)}</TableCell>
                  <TableCell>{translate(`${TP}.updatedAt`)}</TableCell>
                  <TableCell align="right">{translate(`${TP}.actions`)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingProxyList && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {translate(`${TP}.loading`)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loadingProxyList && proxyRecords.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {translate(`${TP}.noRecords`)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {proxyRecords.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.protocol}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.host}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.port}</TableCell>
                    <TableCell>{row.username}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={translate(row.enabled ? `${TP}.enabled` : `${TP}.disabled`)}
                        color={row.enabled ? 'success' : 'default'}
                        variant="soft"
                      />
                    </TableCell>
                    <TableCell>
                      <XDateField fullDate value={row.createdAt} />
                    </TableCell>
                    <TableCell>
                      <XDateField fullDate value={row.updatedAt} />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <PermissionButton resource={Resources.domainConfigs} action="update">
                          <Tooltip title={translate('ra.action.edit')}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingProxy(row);
                                setProxyDialogOpen(true);
                              }}
                            >
                              <Iconify icon="solar:pen-bold" width={16} />
                            </IconButton>
                          </Tooltip>
                        </PermissionButton>
                        <PermissionButton resource={Resources.domainConfigs} action="delete">
                          <Tooltip title={translate('ra.action.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteProxy(row.id)}
                              disabled={deleteProxyLoading === row.id}
                            >
                              <Iconify icon="solar:trash-bin-trash-bold" width={16} />
                            </IconButton>
                          </Tooltip>
                        </PermissionButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* ---- Domain Configs section ---- */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 4, mb: 1 }}
        >
          <Typography variant="h6">{translate(`${T}.domains`)}</Typography>
          <Stack direction="row" spacing={1}>
            <PermissionButton resource={Resources.domainConfigs} action="create">
              <XButton
                variant="contained"
                label={translate(`${T}.addDomain`)}
                onClick={handleOpenAdd}
                sx={{ minWidth: 120 }}
              />
            </PermissionButton>
            <PermissionButton resource={Resources.domainConfigs} action="create">
              <XButton
                variant="outlined"
                label={translate(`${T}.batchAdd`)}
                onClick={() => setBatchOpen(true)}
                sx={{ minWidth: 120 }}
              />
            </PermissionButton>
            <PermissionButton resource={Resources.domainConfigs} action="create">
              <LoadingButton
                variant="outlined"
                loading={csvLoading}
                onClick={() => csvInputRef.current?.click()}
                startIcon={<Iconify icon="eva:upload-fill" />}
              >
                {translate(`${T}.importCsv`)}
              </LoadingButton>
            </PermissionButton>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleCsvChange}
            />
          </Stack>
        </Stack>
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate(`${T}.domain`)}</TableCell>
                  <TableCell>{translate(`${T}.status`)}</TableCell>
                  <TableCell>{translate(`${T}.lastCheck`)}</TableCell>
                  <TableCell>{translate(`${T}.createdAt`)}</TableCell>
                  <TableCell>{translate(`${T}.updatedAt`)}</TableCell>
                  <TableCell align="right">{translate(`${T}.actions`)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingList && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {translate(`${T}.loading`)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loadingList && records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {translate(`${T}.noRecords`)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {records.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.domain}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={translate(row.alive ? `${T}.alive` : `${T}.down`)}
                        color={row.alive ? 'success' : 'error'}
                        variant="soft"
                      />
                    </TableCell>
                    <TableCell>
                      {row.lastCheck ? <XDateField fullDate value={row.lastCheck} /> : '—'}
                    </TableCell>
                    <TableCell>
                      <XDateField fullDate value={row.createdAt} />
                    </TableCell>
                    <TableCell>
                      <XDateField fullDate value={row.updatedAt} />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <PermissionButton resource={Resources.domainConfigs} action="update">
                          <Tooltip title={translate('ra.action.edit')}>
                            <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                              <Iconify icon="solar:pen-bold" width={16} />
                            </IconButton>
                          </Tooltip>
                        </PermissionButton>
                        <PermissionButton resource={Resources.domainConfigs} action="delete">
                          <Tooltip title={translate('ra.action.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(row.id)}
                              disabled={deleteLoading === row.id}
                            >
                              <Iconify icon="solar:trash-bin-trash-bold" width={16} />
                            </IconButton>
                          </Tooltip>
                        </PermissionButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <ProxyDialog
          open={proxyDialogOpen}
          initial={editingProxy}
          loading={proxyDialogLoading}
          onClose={() => setProxyDialogOpen(false)}
          onConfirm={handleProxyDialogConfirm}
        />

        <DomainDialog
          open={dialogOpen}
          initial={editing?.domain ?? ''}
          loading={dialogLoading}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleDialogConfirm}
        />

        <BatchDialog
          open={batchOpen}
          loading={batchLoading}
          onClose={() => setBatchOpen(false)}
          onConfirm={handleBatchConfirm}
        />

        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          resource="domain-configs"
          title={`${translate('resource.auditLogs')} - ${translate(`${T}.title`)}`}
        />
      </DashboardContent>
    </PermissionGuard>
  );
}
